import { Link } from "react-router-dom";
import { Hammer, RefreshCcw, Gem, FileCheck, Sparkles, Sun, Layers, PenLine } from "lucide-react";
import { FadeUp, PageHero, GhostLink } from "../components/Reveal";
import { IMG } from "../lib/content";

const SERVICES = [
  {
    icon: Hammer,
    title: "Jewellery Repairs",
    body: "Our jewellers can advise you about repairing and restoring your jewellery. Bring your piece into the Peel shop or contact us to discuss the work.",
  },
  {
    icon: RefreshCcw,
    title: "Remodelling",
    body: "Transform existing jewellery into something new. We can remodel treasured pieces into designs you will love to wear again — enquire to discuss what is possible.",
  },
  {
    icon: Gem,
    title: "Bespoke Commissions",
    body: "Unique jewellery created around your requirements, with the majority of craftsmanship completed in-house on the Isle of Man.",
    cta: ["Start a Bespoke Enquiry", "/bespoke"],
  },
  {
    icon: FileCheck,
    title: "Jewellery Valuations",
    body: "In-house valuations for insurance replacement and probate, carried out on the Isle of Man. If you are unsure whether your jewellery requires a valuation, we offer a complimentary review of your collection. We can also document your jewellery to help protect you should items be lost or stolen.",
    cta: ["Enquire About a Valuation", "/contact?subject=Valuation enquiry"],
  },
  {
    icon: Sparkles,
    title: "Jewellery Cleaning",
    body: "A dedicated cleaning service to refresh and maintain your treasured pieces.",
  },
  {
    icon: Sun,
    title: "Polishing",
    body: "Professional polishing to restore the appearance of suitable jewellery.",
  },
  {
    icon: Layers,
    title: "Rhodium Plating",
    body: "Rhodium plating gives white gold jewellery a bright, brilliant finish. Available for Celtic Gold jewellery and your own pieces at an additional charge.",
    note: "Rhodium plating wears over time and repeated applications are not recommended.",
  },
  {
    icon: PenLine,
    title: "Engraving",
    body: "Engraving on Celtic Gold jewellery, your own jewellery, trophies, glass and pewter — ideal for gifts, special occasions, anniversaries, weddings, achievements and personal keepsakes.",
    cta: ["Enquire About Engraving", "/contact?subject=Engraving enquiry"],
  },
];

export default function Services() {
  return (
    <main data-testid="services-page">
      <PageHero
        eyebrow="Our Services"
        title="The workshop behind the jewellery"
        sub="Repairs, remodelling, valuations, cleaning, polishing, rhodium plating and engraving — all supported by our fully equipped workshop in Peel."
        image={IMG.workshop}
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => (
            <FadeUp key={s.title} delay={(i % 2) * 0.08}>
              <div className="border border-white/5 hover:border-gold/40 transition-colors duration-500 bg-ink-2/40 p-8 h-full flex flex-col" data-testid={`service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <s.icon className="w-6 h-6 text-gold mb-5" />
                <h2 className="font-serif text-2xl text-ivory font-light mb-3">{s.title}</h2>
                <p className="text-stone-mist text-sm font-light leading-relaxed flex-1">{s.body}</p>
                {s.note && (
                  <p className="mt-4 text-xs text-gold/90 border border-gold/25 bg-gold/5 px-4 py-3 leading-relaxed">{s.note}</p>
                )}
                {s.cta && (
                  <Link to={s.cta[1]} className="mt-6 inline-flex text-gold text-xs uppercase tracking-[0.25em] hover:text-gold-glow transition-colors" data-testid={`service-cta-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    {s.cta[0]} →
                  </Link>
                )}
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mt-16 text-center">
          <p className="text-stone-mist font-light mb-8">Not sure which service you need? Bring your piece in or give us a call — we're happy to advise.</p>
          <GhostLink to="/contact" testId="services-contact">Talk to Us</GhostLink>
        </FadeUp>
      </section>
    </main>
  );
}
